import React, { useState } from "react";
import { useAuth } from "hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
// Duas versões: a branca (assets/logo.png, a mesma da sidebar) para o painel
// azul-marinho, e a azul (public/images) para o topo no mobile, que é branco.
import logoBranca from "../assets/logo.png";

const WHATSAPP_SUPORTE = "https://wa.me/554792104226";

/**
 * Login do painel do cliente.
 *
 * Não há link para cadastro de propósito: conta de cliente é criada
 * internamente pela equipe Intrex. (A rota /SignUpWizard continua existindo e
 * acessível por URL direta — esconder o link não a protege.)
 *
 * Também não há "esqueci minha senha": não existe fluxo de recuperação no
 * backend. Em vez de um link morto para "#", manda falar com o suporte, que é
 * quem resolve hoje.
 */
export const Login: React.FC = () => {
  const [emailPrincipal, setEmailPrincipal] = useState("");
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [mostrarSenha, setMostrarSenha] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErro(null);
    setLoading(true);

    try {
      await login(emailPrincipal, senha);
      // Sem espera artificial: antes havia um setTimeout de 1,6s só para
      // exibir a animação de avião. Loader bonito não compensa segurar o
      // usuário que já está autenticado.
      navigate("/dashboard");
    } catch (e: any) {
      setLoading(false);
      setErro(e?.response?.data?.erro || e?.message || "Falha ao conectar.");
    }
  };

  return (
    <div className="flex min-h-screen font-Inter">
      {/* PAINEL DA MARCA — some no mobile, onde só o formulário importa */}
      <aside className="relative hidden w-[44%] flex-col justify-between overflow-hidden bg-brand-sidebar p-12 lg:flex">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(closest-side, #2563EB, transparent)" }}
        />

        <img
          src={logoBranca}
          alt="Intrex"
          className="relative h-10 w-auto self-start object-contain"
        />

        <div className="relative">
          <h1 className="max-w-md text-4xl font-bold leading-tight tracking-tight text-white">
            Sua operação de exportação em uma tela só
          </h1>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/50">
            Cote UPS e FedEx lado a lado, agende coletas e acompanhe cada envio
            do despacho à entrega.
          </p>
        </div>

        <p className="relative text-xs text-white/30">
          © {new Date().getFullYear()} Intrex · Logística internacional
        </p>
      </aside>

      {/* FORMULÁRIO */}
      <main className="flex flex-1 items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-sm">
          <img
            src="/images/intrex_logo.png"
            alt="Intrex"
            className="mb-10 h-8 w-auto object-contain lg:hidden"
          />

          <h2 className="text-2xl font-bold tracking-tight text-brand-sidebar">
            Entrar
          </h2>
          <p className="mt-1 text-sm text-brand-text/60">
            Acesse o painel da sua operação.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-sm font-medium text-brand-sidebar"
              >
                E-mail
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-text/40" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="voce@empresa.com.br"
                  value={emailPrincipal}
                  onChange={(e) => setEmailPrincipal(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-3 text-sm text-brand-sidebar outline-none transition-colors placeholder:text-brand-text/30 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="senha"
                className="mb-1.5 block text-sm font-medium text-brand-sidebar"
              >
                Senha
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-text/40" />
                <input
                  id="senha"
                  type={mostrarSenha ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-10 text-sm text-brand-sidebar outline-none transition-colors placeholder:text-brand-text/30 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  aria-label={mostrarSenha ? "Ocultar senha" : "Mostrar senha"}
                  onClick={() => setMostrarSenha((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-brand-text/40 transition-colors hover:text-brand-text"
                >
                  {mostrarSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {erro && (
              <p
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {erro}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-primary py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-brand-sidebar disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Entrando..." : "Entrar"}
            </button>
          </form>

          <p className="mt-8 text-center text-xs text-brand-text/50">
            Problemas para acessar?{" "}
            <a
              href={WHATSAPP_SUPORTE}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-brand-primary hover:underline"
            >
              Fale com o suporte
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};
